import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Apply from './Apply';
import { usePageTracking, useFormTracking, useAnalytics } from '../hooks/useAnalytics';
import {
  getApplicantV2ErrorMessage,
  requestResumeEmailCode,
  saveApplicantV2Session,
  verifyResumeEmailCode,
} from '../utils/applicantV2';
import { mapV2ApplicationStateToWizardStep } from '../utils/wizardSteps';

const timeline = [
  { icon: '📋', title: 'Apply Online', description: 'Quick form, driver licence upload, and basic availability.', timing: 'Day 1' },
  { icon: '🗺️', title: 'Complete Your Fit Check', description: 'Answer quick fit questions and complete local knowledge.', timing: 'Same day' },
  { icon: '🎥', title: 'Submit Video + Requirements', description: 'Optional video can help strong applicants move faster.', timing: 'Same day' },
  { icon: '✅', title: 'Get Approved', description: 'Qualified applicants can move forward without waiting on email.', timing: 'Fast review' },
];

const benefits = [
  { title: 'Up to $20–30/hour', description: 'Real earnings including tips. Weekly deposits every Thursday.', tone: 'green', image: 'earnings.webp' },
  { title: 'Your Schedule', description: 'Work when you want, as much as you want. No minimums.', tone: 'purple', image: 'flexible-schedule.webp' },
  { title: 'Stay Local', description: 'Short distances in your own community. Know the roads.', tone: 'green', image: 'stay-local.webp' },
  { title: 'Start Fast', description: 'Our V2 process is built to reduce waiting and email friction.', tone: 'purple', image: 'start-fast.webp' },
];

const requirements = [
  ['Valid Driver’s Licence', 'Required up front so the process can keep moving.'],
  ['Transportation', 'Car, truck, EV, or scooter depending on the area.'],
  ['Smartphone with Data', 'iPhone or Android for the courier app.'],
  ['Insulated Bag', 'Use yours to start while your official kit ships.'],
  ['16+ Years Old', 'The date picker is limited to eligible applicants.'],
  ['Local Knowledge', 'Applicants under 60% must wait 60 days before reapplying.'],
];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeRequested, setCodeRequested] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [error, setError] = useState('');

  usePageTracking('Home');
  const { onFormStart, onFormSubmit } = useFormTracking('email_resume', 'home_page');
  const { trackApplicationStarted, trackAPIError } = useAnalytics();

  const handleRequestCode = async (event: React.FormEvent) => {
    event.preventDefault();
    onFormStart();
    setIsLookingUp(true);
    setError('');
    try {
      await requestResumeEmailCode(email.trim().toLowerCase());
      setCodeRequested(true);
      onFormSubmit(true);
    } catch (requestError: any) {
      trackAPIError({ endpoint: '/v2/applications/resume/challenge', errorCode: requestError.response?.status || 'unknown', errorMessage: requestError.message });
      setError(getApplicantV2ErrorMessage(requestError, 'Unable to send a verification code. Please try again.'));
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleVerifyCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLookingUp(true);
    setError('');
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const verified = await verifyResumeEmailCode(normalizedEmail, code.trim());
      const session = { applicationId: verified.applicationId, sessionToken: verified.sessionToken };
      saveApplicantV2Session(session);
      trackApplicationStarted({ email: normalizedEmail, applicantId: session.applicationId, entrySource: 'email_resume' });
      navigate(`/wizard/${mapV2ApplicationStateToWizardStep(verified.currentStep, verified.applicationStatus)}`);
    } catch (verifyError: any) {
      trackAPIError({ endpoint: '/v2/applications/resume/verify', errorCode: verifyError.response?.status || 'unknown', errorMessage: verifyError.message });
      setError(getApplicantV2ErrorMessage(verifyError, 'Unable to verify that code. Please try again.'));
    } finally {
      setIsLookingUp(false);
    }
  };

  return (
    <div className="app-shell">
      <Header />
      <main className="page-content">
        <section className="courier-hero">
          <div className="courier-hero__overlay" aria-hidden="true" />
          <span>🚗 Most drivers approved in 1–3 days</span>
          <h1>Deliver with Valley Eats</h1>
          <p>Earn up to $20–30/hour on your schedule while serving your community. Start your application below and finish the required steps right away.</p>
          <a href="#application">Apply Now – Start This Week</a>
        </section>

        <section className="landing-section">
          <div className="section-heading">
            <h2>Start Delivering in Days, Not Weeks</h2>
            <p>Our streamlined V2 process is designed to keep applicants moving without relying on email follow-ups.</p>
          </div>
          <div className="timeline-card">
            <h3>Your Quick Timeline</h3>
            <div className="timeline-list">
              {timeline.map((item, index) => (
                <div className="timeline-item" key={item.title}>
                  <span className="timeline-icon" aria-hidden="true">{item.icon}</span>
                  {index < timeline.length - 1 && <span className="timeline-line" aria-hidden="true" />}
                  <div><h4>{item.title}</h4><p>{item.description}</p><strong>{item.timing}</strong></div>
                </div>
              ))}
            </div>
            <div className="timeline-tip">💡 Applicants who complete the local knowledge, hit fit targets, and upload a video can move forward faster.</div>
          </div>
        </section>

        <section className="landing-section benefits-section">
          <div className="section-heading"><h2>Why Drive with Valley Eats?</h2><p>Join a community of local couriers making a difference.</p></div>
          <div className="benefit-grid">
            {benefits.map((benefit) => (
              <article className={`benefit-card benefit-card--${benefit.tone}`} key={benefit.title}>
                <div className="benefit-card__media" aria-hidden="true" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/benefits/${benefit.image})` }} />
                <div><h3>{benefit.title}</h3><p>{benefit.description}</p></div>
              </article>
            ))}
          </div>
        </section>

        <section className="application-card resume-card">
          <div><h2>Already started?</h2><p>Continue your application using the email address you applied with.</p></div>
          <form onSubmit={codeRequested ? handleVerifyCode : handleRequestCode}>
            <label>Email Address<input required type="email" autoComplete="email" disabled={isLookingUp || codeRequested} value={email} onChange={(event) => setEmail(event.target.value)} /></label>
            {codeRequested && <label>6-Digit Code<input required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} /></label>}
            {codeRequested && <p className="resume-message">If that email matches an application, a code was sent. Check your inbox and spam folder.</p>}
            <button className="secondary-button" disabled={isLookingUp || !email || (codeRequested && code.length !== 6)} type="submit">
              {isLookingUp ? 'Please wait…' : codeRequested ? 'Continue Application' : 'Get Verification Code'}
            </button>
            {codeRequested && <button className="text-button" type="button" onClick={() => { setCodeRequested(false); setCode(''); setError(''); }}>Use a different email address</button>}
            {error && <p className="resume-message" role="alert">{error}</p>}
          </form>
        </section>

        <section className="landing-section requirements-application" id="application">
          <div className="requirements-copy">
            <h2>Getting Started is Easy</h2>
            <p>Meet these simple requirements and you’re ready to go.</p>
            <div className="requirements-list">
              {requirements.map(([title, description]) => <div key={title}><span aria-hidden="true">✓</span><strong>{title}</strong><p>{description}</p></div>)}
            </div>
            <div className="kit-callout"><h3>🎁 Your Valley Eats Kit Includes:</h3><ul><li>Professional branded thermal bag</li><li>Valley Eats lanyard</li><li>Quick-start guide</li></ul></div>
          </div>
          <Apply embedded />
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Home;
